import React from "react";
import classNames from "classnames";
import { STEPS } from "./MouseTutorialSteps";
import { MouseAvatar } from "./MouseAvatar";
import { sleep } from "oc-commons/Commons";
import { Zauneditor } from "../../pages/Zauneditor";

/**
 * Eine Exception als Teil des normalen Controllflows, wie ein break
 */
class BreakException extends Error {
  target: string = undefined;

  constructor(target: string) {
    super();
    this.target = target;
  }
}

type Props = {
  zauneditor: Zauneditor;
};

/**
 * StepEngine ist eine spezielle StateEngine, in der die einzelnen States (Steps)
 * eine Reihenfolge haben und als Default nacheinander durchlaufen werden, falls keine
 * Targets definiert sind.
 */
export class MouseTutorialStepEngine extends React.Component<Props> {
  assistantRef = React.createRef();
  element: HTMLElement = null;
  steps = STEPS;
  stateExitListener: Function = null;
  mouseAvatarRef = React.createRef<HTMLDivElement>();
  breakTarget: string = undefined;
  wasSkipped: boolean = false;
  currentStep: any = null;

  state = {
    step: null,
    dialogShowing: false,
  };

  setStep(target: string) {
    this.breakTarget = target;
    if (target === "final" && this.currentStep?.name !== "passed") {
      this.wasSkipped = true;
    }
  }

  onStateExit(callback: Function) {
    this.stateExitListener = callback;
  }

  get mouse() {
    return this.mouseAvatarRef.current;
  }

  get assistant() {
    return this.assistantRef.current;
  }

  async showMouseTutorialOnlyFirstTime(actionBeforeSelect: Function) {
    if (localStorage["mouseTutorialShown"]) {
      this.setState({ ...this.state, step: null });
    } else {
      this.wasSkipped = false;
      await this.showMouseTutorial();
      localStorage["mouseTutorialShown"] = true;
      localStorage["joyrideTourStep"] = JSON.stringify(
        this.wasSkipped ? { grundriss: -1, zauneditor: -1 } : { grundriss: 0, zauneditor: 0 }
      );
      localStorage["breadcrumbsHighlighted"] = JSON.stringify({
        grundriss: false,
        zauneditor: false,
      });
    }
    if (actionBeforeSelect) {
      actionBeforeSelect();
    }
  }

  async showMouseTutorial(): Promise<void> {
    const showPotentiellAblenkendeElemente = (show: boolean) => {
      this.props.zauneditor.showPotentiellAblenkendeElemente(show);
    };
    showPotentiellAblenkendeElemente(false);
    this.setState({ ...this.state, step: this.steps[0] });
    await sleep(100);
    await this._interpretStepLoop();
    showPotentiellAblenkendeElemente(true);
  }

  async _interpretStepLoop(): Promise<void> {
    let stepBefore = null;
    let step = this.state.step;

    const maybeWaitForDialogFade = async (fadeIn = true) => {
      const key = step?.dialog?.key;
      const beforeKey = stepBefore?.dialog?.key;
      if (key != null) {
        APP.logUserAction({ action: "mousetutorial", value: key }, { cached: true });
      }
      if (key == null || beforeKey == null || key != beforeKey) {
        this.setState({ ...this.state, dialogShowing: fadeIn });
        await sleep(300);
      }
    };

    this.props.zauneditor.tutorialMode = true;

    while (step != null) {
      this.currentStep = step;
      let target = step.target;
      APP.controls.enabled = !step.modal;
      this.props.zauneditor.enableZaunSelection = step.enableZaunSelection ?? true;
      if (step.dialog) {
        await maybeWaitForDialogFade(true);
      }
      try {
        if (step.action) {
          await step.action(this);
        }
        if (step.mouseAnimation?.action) {
          await step.mouseAnimation.action(this);
        } else if (step.duration != null) {
          await this.waitOrExit(step.duration || 0);
        } else if (step.target === undefined) {
          while (true) {
            await this.waitOrExit(50);
          }
        }
      } catch (e) {
        if (e instanceof BreakException) {
          if (e.target === undefined) {
            break;
          }
          target = e.target;
        } else {
          throw e;
        }
      }
      if (this.stateExitListener) {
        const callback = this.stateExitListener;
        this.stateExitListener = null;
        await callback();
      }
      stepBefore = step;
      if (target == null) {
        step = this.steps[this.steps.indexOf(this.state.step) + 1];
      } else {
        step = this.steps.find((step) => step.name == target);
        if (step == null) {
          throw new Error(`no step with name "${target}" exists`);
        }
      }
      if (stepBefore.dialog) {
        await maybeWaitForDialogFade(false);
      }
      this.setState({ ...this.state, step });
      await sleep(50);
    }
    APP.controls.enabled = true;
    this.props.zauneditor.enableZaunSelection = true;
    this.props.zauneditor.tutorialMode = false;
  }

  async waitOrExit(ms = 1) {
    const t0 = performance.now();
    while (true) {
      if (this.breakTarget !== undefined) {
        const breakTarget = this.breakTarget;
        this.breakTarget = undefined;
        throw new BreakException(breakTarget);
      }
      await sleep(Math.min(100, ms));
      const t1 = performance.now();
      if (t1 - t0 >= ms) {
        return;
      }
    }
  }

  render() {
    const step = this.state.step;
    return (
      this.state.step != null && (
        <div
          className="mouse-tutorial"
          role="region"
          aria-label={I18N("aria::Interaktives Tutorial")}
        >
          {step.modal && (
            <div className={classNames("mouse-tutorial-modal")} aria-hidden="true"></div>
          )}

          {step.dialog && (
            <div
              className="assistant-container"
              role="complementary"
              aria-label={I18N("aria::Tutorial-Assistent")}
            >
              <div className="assistant-message-and-button">
                <div
                  className="assistant-message shadow-light"
                  role="dialog"
                  aria-live="polite"
                  aria-atomic="true"
                  aria-labelledby="tutorial-message"
                >
                  <div id="tutorial-message" className="text">
                    {step.dialog.text && step.dialog.text()}
                  </div>
                  <div
                    className="button-container"
                    role="group"
                    aria-label={I18N("aria::Tutorial-Aktionen")}
                  >
                    {step.dialog.buttons.map(({ key, label, className, autoFocus, target }) => (
                      <button
                        key={key ?? (label && label())}
                        className={classNames(className, "shadow-light", "pointer-events-initial")}
                        onClick={() => this.setStep(target)}
                        autoFocus={autoFocus}
                        aria-label={label && label()}
                      >
                        {label && label()}
                      </button>
                    ))}
                  </div>
                </div>
                <div
                  className="assistant-avatar assistant-avatar-button shadow-light"
                  role="img"
                  aria-label={I18N("aria::Tutorial-Assistent Avatar")}
                ></div>
              </div>
              <div className="assistant-message-actions">
                <div className="assistant-message-action" aria-hidden="true">
                  &nbsp;
                </div>
              </div>
            </div>
          )}

          <MouseAvatar
            stepEngine={this}
            /* @ts-ignore //TODO: remove when MouseAvater translated to tsx */
            ref={this.mouseAvatarRef}
            className={!this.state.step.mouseAnimation && "hidden"}
            aria-label={I18N("aria::Maus-Animation")}
          />
        </div>
      )
    );
  }
}
